import { notification, Spin } from 'antd'
import { t } from 'i18next'
import React, { createContext, useCallback, useEffect, useState } from 'react'
import { Outlet, useNavigate } from 'react-router-dom'
import { useLocalStorage } from 'react-use'
import { get, post } from '../Common/api'
import { debounce } from '../Common/debounce'
import eventBus from '../Common/EventBus'
import { lbmNotification } from '../Common/notification'
import consumer from '../Common/cable'
import dayjs from 'dayjs'

const GlobalContext = createContext()
export default GlobalContext

export const GlobalContextProvider = ({ children }) => {
  const [data, setData] = useLocalStorage('data', { isAuth: false })
  const [loading, setLoading] = useState(false)
  const [eventOn, setEventOn] = useState(false)
  const [notificationOn, setNotificationOn] = useState(false)
  const [notificationApi, notificationContextHolder] = notification.useNotification()

  // Slots state
  const [slots, setSlots] = useState([])
  const [boxes, setBoxes] = useState([])
  const [slotsDate, setSlotsDate] = useState(dayjs())
  const [slotsLoading, setSlotsLoading] = useState(false)

  const navigate = useNavigate()

  const onNotAuthenticated = useCallback(debounce(() => {
    navigate('/login')
    logout()
  }, 100), [])

  useEffect(() => {
    if (eventOn) { return }
    setEventOn(true)
    eventBus.on('NOT_AUTHENTICATED', onNotAuthenticated)
    return () => {
      eventBus.remove('NOT_AUTHENTICATED', onNotAuthenticated)
    }
  }, [])

  useEffect(() => {
    if (data.isAuth && !notificationOn) {
      setNotificationOn(true)
      consumer.subscriptions.create({ channel: 'NotifyChannel' }, {
        connected: () => { console.log('connected NotifyChannel') },
        disconnected: () => { },
        received: (data) => { lbmNotification(navigate, data, notificationApi) }
      })
    }
  }, [])

  const login = async (params) => {
    setLoading(true)
    await post({
      url: 'admin/login',
      params,
      notification: { api: notificationApi, state: 'login' },
      onSuccess: (response) => {
        const url = params.fabrication ? '/commands' : '/admin'
        setData({ isAuth: true, user: response.user })
        notification.success({ message: response.message, placement: 'bottom' })
        window.location.href = url
      },
      onFailure: (response) => {
        notification.error({ message: response.error, placement: 'bottom' })
      }
    })
    setLoading(false)
  }

  const logout = () => {
    setData({ isAuth: false })
  }

  const updatePassword = async (params) => {
    setLoading(true)
    await post({
      url: 'admin/users/update_password',
      params,
      notification: { api: notificationApi, state: 'password_update' },
      onSuccess: () => {
        navigate('/admin/login')
      }
    })
    setLoading(false)
  }

  const checkToken = async (params) => {
    setLoading(true)
    await post({
      url: 'admin/users/update_password',
      params,
      onSuccess: () => {},
      onFailure: (response) => {
        if (Object.keys(response.errors).includes('reset_password_token')) {
          notificationApi.error({ key: 'request_response', message: 'Lien expiré', placement: 'bottom' })
          navigate('/admin/login')
        }
      }
    })
    setLoading(false)
  }

  const errorMessages = (errors, traductionPath) => {
    return Object.keys(errors).map(key => (
      <>{t(traductionPath + '.' + key) + ': ' + errors[key]}</>
    ))
  }

  // Slots functions
  const fetchSlots = async (date) => {
    setSlotsLoading(true)
    setSlotsDate(date)
    await get({
      url: 'admin/slots',
      params: { scope: 'filtered_list', date: date.format() },
      onSuccess: (res) => {
        setSlotsLoading(false)
        setSlots(res.slots.map((slot) => ({ ...slot, assigned_short_name: !slot.assigned_short_name ? '' : slot.assigned_short_name })))
        setBoxes(res.boxes)
      }
    })
  }

  const updateSlots = (updatedSlot) => {
    if (updatedSlot?.id) {
      setSlots(prev => {
        if (prev.find((e) => e.id === updatedSlot.id)) {
          return prev.map((s) => (s.id === updatedSlot.id) ? { ...s, ...updatedSlot } : s)
        } else {
          return [...prev, updatedSlot]
        }
      })
    }
  }

  const updateBoxes = (updatedBox) => {
    if (updatedBox?.id) {
      setBoxes(prev => {
        if (prev.find((e) => e.id === updatedBox.id)) {
          return prev.map((b) => (b.id === updatedBox.id) ? { ...b, ...updatedBox } : b)
        }
        return prev
      })
    }
  }

  const values = {
    setData,
    data,
    login,
    logout,
    updatePassword,
    checkToken,
    errorMessages,
    notificationApi,
    // Slots
    slots,
    boxes,
    slotsDate,
    slotsLoading,
    fetchSlots,
    updateSlots,
    updateBoxes
  }
  return <GlobalContext.Provider value={values}>
    <Spin spinning={loading}>
      {notificationContextHolder}
      { children || <Outlet /> }
    </Spin>
  </GlobalContext.Provider>
}
